import { createError, dateToMonthYear, isMonthYearAfterOrEqual } from '@common/neo.tax';
import { Stack } from '@ui-components/neo.tax';
import React, { useMemo } from 'react';
import { Outlet } from 'react-router-dom';

import {
  getFiscalYearSubsetPeriod,
  useFiscalYearOrFail,
  PeriodName,
  useSelectedProductOptional,
  ProductType,
  useSelectedFiscalYearIdOptional,
  useSelectedPeriodNameOptional,
} from '@neo.tax/shared';
import { SredContextProvider } from '@neo.tax/sred/context';

import { SredSideNav } from '../sred-side-nav';
import { Container } from './sred-home-layout.styled';

export const SredHomeLayout = (): React.ReactElement | null => {
  const product = useSelectedProductOptional();
  const periodName = useSelectedPeriodNameOptional();
  const fiscalYearId = useSelectedFiscalYearIdOptional();
  if (product !== ProductType.CaSred || !periodName || !fiscalYearId) {
    throw createError('SredHomeLayoutError', 'Product is not SRED or period name or fiscal year id not found');
  }

  if (periodName !== PeriodName.A) {
    throw createError('SredHomeLayoutError', 'Period is not annual for SR&ED');
  }

  const { fiscalYear } = useFiscalYearOrFail({ fiscalYearId });

  const { isNowPastPeriodEndDate, period } = useMemo(() => {
    const period = getFiscalYearSubsetPeriod(fiscalYear.taxYearStartDate, fiscalYear.taxYearEndDate, periodName);
    const today = new Date();
    const isNowPastPeriodEndDate = isMonthYearAfterOrEqual(dateToMonthYear(today), period.endMonthYear);
    return { period, isNowPastPeriodEndDate };
  }, [fiscalYear.taxYearStartDate, fiscalYear.taxYearEndDate, periodName]);

  const sredTaxCreditId = fiscalYear.sredTaxCredit?.id;
  if (!sredTaxCreditId) {
    throw createError('SredHomeLayoutError', 'SR&ED tax credit not found for fiscal year');
  }

  return (
    <SredContextProvider
      fiscalYearId={fiscalYearId}
      fiscalYear={fiscalYear}
      periodName={periodName}
      period={period}
      product={product}
      isNowPastPeriodEndDate={isNowPastPeriodEndDate}
      sredTaxCreditId={sredTaxCreditId}
    >
      <Container>
        <SredSideNav />
        <Stack fullHeight fullWidth overflow="y-auto">
          <Outlet />
        </Stack>
      </Container>
    </SredContextProvider>
  );
};
